import React, { useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import toggle from 'lodash/xor';
import styled, { css } from 'styled-components';
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionItemBodyWithAnimation,
  AccordionItemHeading,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  ColorInput,
  RadioButton,
  RadioGroup,
  Slider,
} from '@sparknz/spark-pattern-library';
import { LinkRegion, trackCustomLink } from '@sparknz/tracking';

import { DeviceGalleryQuery } from '__generated__/DeviceGalleryQuery.graphql';
import { useQuery } from 'shared/hooks';
import { convertToArray } from 'shared/utils';
import {
  BrandProps,
  ColorProps,
  CompatibleSimProps,
  FilterIds,
  NetworkCapabilityProps,
  SortOption,
} from '../../typings';
import {
  FALLBACK_SORT_VALUE,
  PRICE_RANGE_STEP,
  removeUnavailableFilterOptions,
  SortOptions,
} from '../../utils/applyFilters.utils';

const DEFAULT_SORT_OPTION = 'featured';

const MIN_SLIDER_VALUE = 0;

const ViewButtonContainer = styled.div`
  ${({ theme: { media, palette, spacing, zIndex, elevation } }) => css`
    box-shadow: ${elevation.topShadow};
    margin: 0 -${spacing.s3};
    padding: ${spacing.s2} ${spacing.s3};

    position: sticky;
    bottom: 0;
    z-index: ${zIndex.modal};

    background-color: ${palette.baseColors.wairua};
    ${media.up('slim')} {
      margin: 0 -${spacing.s4};
      padding: ${spacing.s2} ${spacing.s4};
    }

    ${media.up('big')} {
      margin: 0 -${spacing.s6};
      padding: ${spacing.s2} ${spacing.s6};
    }
  `}
`;

const ResetButton = styled(Button)`
  ${({ theme: { spacing } }) => css`
    padding-top: ${spacing.s3};
  `}
`;

interface MoreFilterProps
  extends BrandProps,
    NetworkCapabilityProps,
    CompatibleSimProps,
    ColorProps {
  filterResultCount: number;
  filters: DeviceGalleryQuery['response']['deviceGallery']['filters'];
  products: DeviceGalleryQuery['response']['deviceGallery']['products'];
  priceOptions: number[];
  onCloseAllFilters: () => void;
}

const formatLimit = (value: number): string => {
  return `$${value}`;
};
const formatLabel = (value: number): string => {
  return `$${MIN_SLIDER_VALUE} - $${value}`;
};

const MoreFilters = ({
  filters,
  products,
  brandOptions,
  colorOptions,
  priceOptions,
  compatibleSimOptions,
  networkCapabilityOptions,
  selectedCompatibleSimOptions,
  selectedNetworkCapabilityOptions,
  selectedBrandOptions,
  selectedColorOptions,
  filterResultCount,
  onCloseAllFilters,
}: MoreFilterProps) => {
  const { replaceQuery, query } = useQuery();
  const [currentExpandedItems, setCurrentExpandedItems] = useState<string[]>(
    [],
  );
  const hasAppliedFilters = !isEmpty(omit(query, 'category'));
  const highestPriceOption = priceOptions[priceOptions.length - 1];

  function handleSortChange(option: SortOption) {
    replaceQuery({
      ...query,
      sort: option?.value || FALLBACK_SORT_VALUE,
    });
  }

  function handleFilterCheck(filter: FilterIds, optionValue: string) {
    const newFilterQuery = {
      ...query,
      [filter]: toggle(convertToArray(query[filter]), [optionValue]),
    };

    replaceQuery(
      removeUnavailableFilterOptions(filters, products, newFilterQuery),
    );
  }

  function handlePriceChange(newAmount: number) {
    replaceQuery({
      ...query,
      price: newAmount,
    });
  }

  function handleResetClick() {
    trackCustomLink('Reset');
    replaceQuery(pick(query, ['categoryId']));
  }

  function handleViewResultsClick() {
    trackCustomLink('View Result');
    onCloseAllFilters();
  }

  return (
    <>
      <Box pb={['s2', 's2', 's4']} data-testid="AllFilters">
        <LinkRegion regionId="advancedFilter">
          {hasAppliedFilters ? (
            <ResetButton variant="textbutton" onClick={handleResetClick}>
              Reset
            </ResetButton>
          ) : null}
        </LinkRegion>
        <Accordion
          allowMultipleExpanded
          onChange={(expandedItems: string[]) =>
            setCurrentExpandedItems(expandedItems)
          }
        >
          {brandOptions ? (
            <LinkRegion regionId="advancedFilter.brand">
              <AccordionItem
                data-testid="BrandFilters"
                key={FilterIds.Brand}
                uuid={FilterIds.Brand}
                isOpen={!currentExpandedItems.includes(FilterIds.Brand)}
              >
                <AccordionItemHeading>
                  Brand
                  <AccordionButton />
                </AccordionItemHeading>
                <AccordionItemBodyWithAnimation
                  isOpen={!currentExpandedItems.includes(FilterIds.Brand)}
                >
                  <CheckboxGroup>
                    {brandOptions.map(({ value, label }) => (
                      <Checkbox
                        key={value}
                        onClick={() => {
                          trackCustomLink(label);
                        }}
                        checked={Boolean(
                          selectedBrandOptions.find(
                            (option) => option.value === value,
                          ),
                        )}
                        onCheck={() =>
                          handleFilterCheck(FilterIds.Brand, value)
                        }
                        label={label}
                        name="brand_filter_group"
                      />
                    ))}
                  </CheckboxGroup>
                </AccordionItemBodyWithAnimation>
              </AccordionItem>
            </LinkRegion>
          ) : null}
          {colorOptions ? (
            <LinkRegion regionId="advancedFilter.colour">
              <AccordionItem
                data-testId="ColorFilters"
                key={FilterIds.PrimaryColor}
                uuid={FilterIds.PrimaryColor}
                isOpen={currentExpandedItems.includes(FilterIds.PrimaryColor)}
              >
                <AccordionItemHeading>
                  Colour
                  <AccordionButton />
                </AccordionItemHeading>
                <AccordionItemBodyWithAnimation
                  isOpen={currentExpandedItems.includes(FilterIds.PrimaryColor)}
                >
                  <CheckboxGroup>
                    {colorOptions.map(({ value, label }) => {
                      return (
                        <ColorInput
                          key={value}
                          color={value}
                          checked={Boolean(
                            selectedColorOptions.find(
                              (option) => option.value === value,
                            ),
                          )}
                          onCheck={() =>
                            handleFilterCheck(FilterIds.PrimaryColor, value)
                          }
                          label={label}
                          name="color_filter_group"
                          inputProps={{
                            onClick: () => {
                              trackCustomLink(label);
                            },
                          }}
                        />
                      );
                    })}
                  </CheckboxGroup>
                </AccordionItemBodyWithAnimation>
              </AccordionItem>
            </LinkRegion>
          ) : null}
          {compatibleSimOptions ? (
            <LinkRegion regionId="advancedFilter.compatibleSim">
              <AccordionItem
                data-testid="CompatibleSimFilters"
                key={FilterIds.CompatibleSim}
                uuid={FilterIds.CompatibleSim}
                isOpen={currentExpandedItems.includes(FilterIds.CompatibleSim)}
              >
                <AccordionItemHeading>
                  SIM Type
                  <AccordionButton />
                </AccordionItemHeading>
                <AccordionItemBodyWithAnimation
                  isOpen={currentExpandedItems.includes(
                    FilterIds.CompatibleSim,
                  )}
                >
                  <CheckboxGroup>
                    {compatibleSimOptions.map(({ value, label }) => (
                      <Checkbox
                        key={value}
                        checked={Boolean(
                          selectedCompatibleSimOptions.find(
                            (option) => option.value === value,
                          ),
                        )}
                        onCheck={() =>
                          handleFilterCheck(FilterIds.CompatibleSim, value)
                        }
                        label={label}
                        name="compatible_sim_filter_group"
                      />
                    ))}
                  </CheckboxGroup>
                </AccordionItemBodyWithAnimation>
              </AccordionItem>
            </LinkRegion>
          ) : null}
          {networkCapabilityOptions ? (
            <LinkRegion regionId="advancedFilter.networkCapability">
              <AccordionItem
                data-testid="NetworkCapabilityFilters"
                key={FilterIds.NetworkCapability}
                uuid={FilterIds.NetworkCapability}
                isOpen={currentExpandedItems.includes(
                  FilterIds.NetworkCapability,
                )}
              >
                <AccordionItemHeading>
                  Mobile Network
                  <AccordionButton />
                </AccordionItemHeading>
                <AccordionItemBodyWithAnimation
                  isOpen={currentExpandedItems.includes(
                    FilterIds.NetworkCapability,
                  )}
                >
                  <CheckboxGroup>
                    {networkCapabilityOptions.map(({ value, label }) => (
                      <Checkbox
                        key={value}
                        checked={Boolean(
                          selectedNetworkCapabilityOptions.find(
                            (option) => option.value === value,
                          ),
                        )}
                        onCheck={() =>
                          handleFilterCheck(FilterIds.NetworkCapability, value)
                        }
                        label={label}
                        name="network_capability_filter_group"
                      />
                    ))}
                  </CheckboxGroup>
                </AccordionItemBodyWithAnimation>
              </AccordionItem>
            </LinkRegion>
          ) : null}
          <LinkRegion regionId="advancedFilter.price">
            <AccordionItem
              data-testid="PriceFilters"
              key="price"
              uuid="price"
              isOpen={currentExpandedItems.includes(FilterIds.Price)}
            >
              <AccordionItemHeading>
                Price
                <AccordionButton />
              </AccordionItemHeading>
              <AccordionItemBodyWithAnimation
                isOpen={currentExpandedItems.includes(FilterIds.Price)}
              >
                <Box>
                  <Slider
                    className="slider"
                    key={highestPriceOption}
                    formatLabel={formatLabel}
                    formatLimit={formatLimit}
                    max={highestPriceOption}
                    min={MIN_SLIDER_VALUE}
                    step={PRICE_RANGE_STEP}
                    onChange={(newAmount: number) =>
                      handlePriceChange(newAmount)
                    }
                    value={Number(query.price) || highestPriceOption}
                    inputProps={{
                      'aria-label': 'price-filter',
                      onClick: () => {
                        trackCustomLink(query.price.toString());
                      },
                    }}
                  />
                </Box>
              </AccordionItemBodyWithAnimation>
            </AccordionItem>
          </LinkRegion>
          <LinkRegion regionId="advancedFilter.sorting">
            <AccordionItem
              data-testid="SortOptions"
              key="sort"
              uuid="sort"
              isOpen={currentExpandedItems.includes(FilterIds.Sort)}
            >
              <AccordionItemHeading>
                Sort by
                <AccordionButton />
              </AccordionItemHeading>
              <AccordionItemBodyWithAnimation
                isOpen={currentExpandedItems.includes(FilterIds.Sort)}
              >
                <RadioGroup>
                  {SortOptions.map((sortOption) => (
                    <RadioButton
                      key={sortOption.value}
                      checked={
                        (query.sort || DEFAULT_SORT_OPTION) === sortOption.value
                      }
                      value={sortOption.value}
                      text={sortOption.label}
                      name="Sort by"
                      onChange={() => handleSortChange(sortOption)}
                      inputProps={{
                        onClick: () => {
                          trackCustomLink(sortOption.value);
                        },
                      }}
                    />
                  ))}
                </RadioGroup>
              </AccordionItemBodyWithAnimation>
            </AccordionItem>
          </LinkRegion>
        </Accordion>
      </Box>
      <ViewButtonContainer>
        <LinkRegion regionId="advancedFilter">
          <Button fullWidth variant="primary" onClick={handleViewResultsClick}>
            {`View ${filterResultCount} results`}
          </Button>
        </LinkRegion>
      </ViewButtonContainer>
    </>
  );
};

export default MoreFilters;
